import { useState } from 'react';
import { type Page } from '@gorilla/portal-shared/src/schemas';
import { TextField, Box, Flex, Text, IconButton, Button, Tooltip, Icon, Toggle } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Heading } from '@vibe/core';
import { CloseSmall, Settings, Downgrade, Alert } from '@vibe/icons';
import { PAGE_ICONS } from '../lib/page-icons';
import { ExternalLink } from './ExternalLink';

// page aliases are used to build client-facing page URLs, so restrict them to URL-safe characters
const PAGE_ALIAS_INVALID_CHARS_REGEX = /[^a-zA-Z0-9-]/g;

export const PAGE_NAME_MAX_LENGTH = 20;

type PageSettingsModalProps = {
  page: Page;
  existingAliases: string[];
  onSave: (updatedPage: Page) => void;
  onClose: () => void;
};

export function PageSettingsModal({ page, existingAliases, onSave, onClose }: PageSettingsModalProps) {
  const [draftPage, setDraftPage] = useState<Page>(page);

  let aliasError = null;

  if (!draftPage.id) {
    aliasError = 'Page alias is required';
  } else if (existingAliases.includes(draftPage.id)) {
    aliasError = 'This alias is already in use';
  } else if (draftPage.id === 'start') {
    aliasError = 'This alias is reserved';
  }

  const handleSave = () => {
    if (aliasError) {
      return;
    }

    onSave(draftPage);
    onClose();
  };

  return (
    <AppModal show={true} onClose={onClose} contentSpacing={false} alertDialog={false} width={397}>
      <AppModalContent>
        <div
          style={{
            marginBottom: '4px',
          }}
        >
          <Flex direction="row" align="center" gap={8} style={{ marginBottom: '20px' }}>
            <Icon icon={Settings} size={24} />
            <Heading type="h3" weight="bold" align="start">
              Page settings
            </Heading>
          </Flex>
          <Flex gap={16} direction="column">
            <Flex gap={16} direction="row" justify="start" style={{ width: '100%' }}>
              <TextField
                size="medium"
                title="Page name"
                placeholder="Page name"
                value={draftPage.name}
                maxLength={PAGE_NAME_MAX_LENGTH}
                onChange={(newName: any) => {
                  setDraftPage({ ...draftPage, name: newName });
                }}
              />
              <TextField
                size="medium"
                title="Page alias"
                placeholder="Page alias"
                value={draftPage.id}
                maxLength={20}
                controlled
                validation={aliasError ? { status: 'error' } : undefined}
                iconTooltipContent={aliasError ? aliasError : ''}
                icon={aliasError ? Alert : undefined}
                onChange={(newAlias: any) => {
                  setDraftPage({ ...draftPage, id: String(newAlias).replace(PAGE_ALIAS_INVALID_CHARS_REGEX, '').toLowerCase() });
                }}
              />
            </Flex>
            <Box
              style={{
                // @ts-ignore
                pointerEvents: draftPage.menu === 'footer' ? 'none' : 'auto',
                width: '100%',
              }}
            >
              <Box padding="medium" border={true} borderColor="layoutBorderColor" rounded="small" backgroundColor="greyBackgroundColor">
                <Text
                  type="text2"
                  weight="normal"
                  align="start"
                  color="primary"
                  style={{
                    marginTop: '-6px',
                    // @ts-ignore
                    opacity: draftPage.menu === 'footer' ? 0.5 : 1,
                  }}
                >
                  Page icon
                </Text>
                <Flex
                  gap={8}
                  wrap
                  direction="row"
                  align="center"
                  justify="start"
                  // @ts-ignore
                  style={{ marginTop: '8px', width: '100%', opacity: draftPage.menu === 'footer' ? 0.5 : 1 }}
                >
                  {PAGE_ICONS.map((icon) => (
                    <div
                      key={icon.id}
                      className={`page__icon__picker ${draftPage.icon === icon.id ? 'page__icon__picker--active' : ''}`}
                      onClick={() => {
                        setDraftPage({ ...draftPage, icon: icon.id });
                      }}
                    >
                      <Tooltip content={icon.id} position="top" zIndex={10000} showDelay={500}>
                        <div className="page__icon__picker__svg">
                          <i className={`fa-regular fa-${icon.id}`}></i>
                        </div>
                      </Tooltip>
                    </div>
                  ))}
                </Flex>
                <Box
                  style={{
                    marginTop: '16px',
                  }}
                >
                  <Text
                    type="text3"
                    weight="normal"
                    align="start"
                    color="secondary"
                    ellipsis={false}
                    style={{
                      marginBottom: '16px',
                      // @ts-ignore
                      opacity: draftPage.menu === 'footer' ? 0.5 : 1,
                    }}
                  >
                    Choose one from the default icons above or paste a specific <strong>Icon Name</strong> (e.g. box-archive) from the{' '}
                    <ExternalLink href="https://fontawesome.com/v6/search?ip=classic&s=regular&o=r" target="_blank">
                      Font Awesome
                    </ExternalLink>{' '}
                    website below. To display the compact menu on a collapsed sidebar, a page icon is required for each main page.
                  </Text>
                  <div
                    style={{
                      position: 'relative',
                    }}
                  >
                    <TextField
                      size="medium"
                      placeholder="e.g. circle-check"
                      value={draftPage.icon || ''}
                      clearOnIconClick
                      // @ts-ignore
                      disabled={draftPage.menu === 'footer'}
                      icon={draftPage.icon ? CloseSmall : null}
                      onChange={(value) => {
                        setDraftPage({ ...draftPage, icon: value });
                      }}
                      className={draftPage.icon ? 'page__icon__picker__input--valid' : ''}
                    />
                    {draftPage.icon ? (
                      <div
                        style={{
                          pointerEvents: 'none',
                          position: 'absolute',
                          left: '4px',
                          top: '4px',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <i className={`fa-regular fa-${draftPage.icon}`} style={{ fontSize: '14px' }}></i>
                      </div>
                    ) : null}
                  </div>
                </Box>
              </Box>
            </Box>
            <Flex gap={16} direction="row" align="center" justify="start" style={{ width: '100%' }}>
              <Box style={{ width: '100%' }}>
                <Flex
                  justify="start"
                  gap={6}
                  style={{
                    flex: 1,
                  }}
                >
                  <Box
                    style={{
                      marginLeft: '-8px',
                    }}
                  >
                    <Toggle
                      id="enabled"
                      offOverrideText=""
                      onOverrideText=""
                      size="small"
                      isSelected={draftPage.menu === 'footer'}
                      onChange={(value: any) => {
                        setDraftPage({ ...draftPage, menu: value ? 'footer' : 'main' });
                      }}
                    />
                  </Box>
                  <Text type="text2" weight="normal" align="end" color={draftPage.menu === 'footer' ? 'primary' : 'secondary'}>
                    Move page to <Icon icon={Downgrade} style={{ transform: 'translate3d(0,3px,0)' }} /> Footer navigation
                  </Text>
                </Flex>
              </Box>
              <Button kind="primary" size="medium" color="primary" disabled={!!aliasError} onClick={handleSave}>
                Save
              </Button>
            </Flex>
          </Flex>
        </div>
      </AppModalContent>
    </AppModal>
  );
}
